import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AccountSidebar from '../components/AccountSidebar';
import { getPlanLimits, formatPlanName } from '../utils/planAccess';
import api from '../api';

// Manage Alerts Modal Component
const ManageAlertsModal = ({ isOpen, onClose, planLimits }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingAlert, setEditingAlert] = useState(null);
  const [editForm, setEditForm] = useState({ target_price: '', alert_type: 'above' });
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(null);

  // Fetch alerts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAlerts();
    }
  }, [isOpen]);

           const fetchAlerts = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/api/v1/watchlist/alerts');
        setAlerts(response.data);
         } catch (error) {
       let errorMessage = 'Failed to fetch alerts.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       setError(errorMessage);
     } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    setDeleteLoading(alertId);
    try {
      await api.delete(`/api/v1/watchlist/alerts/${alertId}`);
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
         } catch (error) {
       let errorMessage = 'Failed to delete alert.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       alert(errorMessage);
     } finally {
      setDeleteLoading(null);
    }
  };

  const handleEditClick = (alert) => {
    setEditingAlert(alert.id);
    setEditForm({
      target_price: alert.target_price.toString(),
      alert_type: alert.alert_type
    });
  };

  const handleCancelEdit = () => {
    setEditingAlert(null);
    setEditForm({ target_price: '', alert_type: 'above' });
  };

  const handleUpdateAlert = async (alertId) => {
    if (!editForm.target_price || editForm.target_price <= 0) {
      alert('Please enter a valid target price');
      return;
    }

    setUpdateLoading(alertId);
    try {
      const response = await api.put(`/api/v1/watchlist/alerts/${alertId}`, {
        target_price: parseFloat(editForm.target_price),
        alert_type: editForm.alert_type
      });
      
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId ? response.data : alert
      ));
      setEditingAlert(null);
      setEditForm({ target_price: '', alert_type: 'above' });
         } catch (error) {
       let errorMessage = 'Failed to update alert.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       alert(errorMessage);
     } finally {
      setUpdateLoading(null);
    }
  };

  const formatAlertCondition = (alert) => {
    const condition = alert.alert_type === 'above' ? '>' : '<';
    return `Price ${condition} ₹${alert.target_price.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Manage Your Price Alerts
            </h3>
            <div className="text-sm text-blue-600 mt-1">
              Limit: {alerts.length} / {planLimits.priceAlerts} alerts
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600">Loading alerts...</span>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔔</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No price alerts found</h3>
              <p className="text-gray-600">You haven't set any price alerts yet. Add stocks to your watchlist and create alerts to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                      Stock Symbol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>
                      Alert Condition
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                      Created Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {alerts.map((alert) => (
                    <tr key={alert.id} className="hover:bg-gray-50">
                                             <td className="px-6 py-4 whitespace-nowrap">
                         <div className="text-sm font-medium text-gray-900">
                           {alert.stock_symbol}
                         </div>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingAlert === alert.id ? (
                          <div className="flex space-x-2">
                            <select
                              value={editForm.alert_type}
                              onChange={(e) => setEditForm(prev => ({ ...prev, alert_type: e.target.value }))}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="above">Above</option>
                              <option value="below">Below</option>
                            </select>
                            <input
                              type="number"
                              value={editForm.target_price}
                              onChange={(e) => setEditForm(prev => ({ ...prev, target_price: e.target.value }))}
                              className="text-sm border border-gray-300 rounded px-2 py-1 w-20"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {formatAlertCondition(alert)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDate(alert.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          alert.triggered 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {alert.triggered ? 'Triggered' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {editingAlert === alert.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleUpdateAlert(alert.id)}
                              disabled={updateLoading === alert.id}
                              className="text-green-600 hover:text-green-900 transition-colors disabled:opacity-50"
                            >
                              {updateLoading === alert.id ? (
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-1"></div>
                                  Saving...
                                </div>
                              ) : (
                                'Save'
                              )}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={updateLoading === alert.id}
                              className="text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-3">
                            <button
                              onClick={() => handleEditClick(alert)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              Modify
                            </button>
                            <button
                              onClick={() => handleDeleteAlert(alert.id)}
                              disabled={deleteLoading === alert.id}
                              className="text-red-600 hover:text-red-900 transition-colors disabled:opacity-50"
                            >
                              {deleteLoading === alert.id ? (
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                  Deleting...
                                </div>
                              ) : (
                                'Delete'
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add Stock Modal Component
const AddStockModal = ({ isOpen, onClose, onSuccess, planLimits, currentCount }) => {
  const [stockSymbol, setStockSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stockSymbol.trim()) {
      setError('Please enter a stock symbol');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/v1/watchlist/', {
        stock_symbol: stockSymbol.trim().toUpperCase()
      });

      onSuccess(response.data);
      setStockSymbol('');
      onClose();
         } catch (error) {
       let errorMessage = 'Failed to add stock to watchlist.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.response?.data?.message) {
         errorMessage = String(error.response.data.message);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       setError(errorMessage);
     } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Add Stock to Watchlist
        </h3>
        
        {/* Plan Limit Info */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm text-blue-800">
            <span className="font-medium">Current Usage:</span> {currentCount} / {planLimits.watchlistItems} items
          </div>
          {!planLimits.isUnlimitedWatchlist && currentCount >= planLimits.watchlistItems && (
            <div className="text-sm text-orange-600 mt-1">
              ⚠️ You've reached your watchlist limit. Upgrade your plan to add more items.
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="stockSymbol" className="block text-sm font-medium text-gray-700 mb-2">
              Stock Symbol
            </label>
            <input
              type="text"
              id="stockSymbol"
              value={stockSymbol}
              onChange={(e) => setStockSymbol(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="e.g., AAPL, GOOGL"
              disabled={loading}
            />
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </div>
              ) : (
                'Add Stock'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Alert Modal Component
const AddAlertModal = ({ isOpen, onClose, stockSymbol, itemId, onSuccess, planLimits }) => {
  const [targetPrice, setTargetPrice] = useState('');
  const [alertType, setAlertType] = useState('above');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetPrice || targetPrice <= 0) {
      setError('Please enter a valid target price');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/api/v1/watchlist/${itemId}/alerts`, {
        target_price: parseFloat(targetPrice),
        alert_type: alertType
      });

      onSuccess(response.data);
      setTargetPrice('');
      setAlertType('above');
      onClose();
         } catch (error) {
       let errorMessage = 'Failed to create price alert.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.response?.data?.message) {
         errorMessage = String(error.response.data.message);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       setError(errorMessage);
     } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Set Alert for {stockSymbol}
        </h3>
        
        {/* Plan Limit Info */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm text-blue-800">
            <span className="font-medium">Price Alerts Limit:</span> {planLimits.priceAlerts}
          </div>
          {!planLimits.isUnlimitedAlerts && (
            <div className="text-sm text-blue-700 mt-1">
              You can create up to {planLimits.priceAlerts} price alerts with your current plan.
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="alertType" className="block text-sm font-medium text-gray-700 mb-2">
              Alert Type
            </label>
            <select
              id="alertType"
              value={alertType}
              onChange={(e) => setAlertType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="above">Above Target Price</option>
              <option value="below">Below Target Price</option>
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="targetPrice" className="block text-sm font-medium text-gray-700 mb-2">
              Target Price (₹)
            </label>
            <input
              type="number"
              id="targetPrice"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="e.g., 150.00"
              step="0.01"
              min="0"
              disabled={loading}
            />
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Setting Alert...
                </div>
              ) : (
                'Set Alert'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {title}
        </h3>
        <p className="text-gray-600 mb-6">
          {message}
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Watchlist Content Component
const WatchlistContent = () => {
  const { user } = useAuth();
  const planLimits = getPlanLimits(user?.subscription_plan);
  const [watchlist, setWatchlist] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Modal states
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showAddAlertModal, setShowAddAlertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showManageAlertsModal, setShowManageAlertsModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Alert modal data
  const [alertModalData, setAlertModalData] = useState({ stockSymbol: '', itemId: null });
  const [deleteModalData, setDeleteModalData] = useState({ itemId: null, stockSymbol: '' });

  // Fetch watchlist on component mount
  useEffect(() => {
    fetchWatchlist();
  }, []);

  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/watchlist/');
      setWatchlist(response.data);
         } catch (error) {
       let errorMessage = 'Failed to fetch watchlist.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       setMessage({ type: 'error', text: errorMessage });
     } finally {
      setLoading(false);
    }
  };

  const handleAddStockSuccess = (newStock) => {
    setWatchlist(prev => [...prev, newStock]);
    setMessage({ type: 'success', text: `${newStock.stock_symbol} added to watchlist successfully!` });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleAddAlertSuccess = (newAlert) => {
    setMessage({ type: 'success', text: 'Price alert created successfully!' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === watchlist.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(watchlist.map(item => item.id));
    }
  };

  const handleDeleteItem = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/api/v1/watchlist/${deleteModalData.itemId}`);
      setWatchlist(prev => prev.filter(item => item.id !== deleteModalData.itemId));
      setMessage({ type: 'success', text: `${deleteModalData.stockSymbol} removed from watchlist successfully!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      setShowDeleteModal(false);
         } catch (error) {
       let errorMessage = 'Failed to delete stock from watchlist.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       setMessage({ type: 'error', text: errorMessage });
     } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      await api.post('/api/v1/watchlist/bulk-delete', {
        item_ids: selectedItems
      });
      setWatchlist(prev => prev.filter(item => !selectedItems.includes(item.id)));
      setSelectedItems([]);
      setMessage({ type: 'success', text: `Successfully deleted ${selectedItems.length} items from watchlist!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      setShowBulkDeleteModal(false);
         } catch (error) {
       let errorMessage = 'Failed to delete selected items.';
       if (error.response?.data?.detail) {
         errorMessage = String(error.response.data.detail);
       } else if (error.message) {
         errorMessage = String(error.message);
       }
       setMessage({ type: 'error', text: errorMessage });
     } finally {
      setBulkDeleteLoading(false);
    }
  };

  const openAddAlertModal = (stockSymbol, itemId) => {
    setAlertModalData({ stockSymbol, itemId });
    setShowAddAlertModal(true);
  };

  const openDeleteModal = (stockSymbol, itemId) => {
    setDeleteModalData({ stockSymbol, itemId });
    setShowDeleteModal(true);
  };

  return (
    <div className="bg-white rounded-r-2xl p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Watchlist</h1>
        <div className="flex items-center justify-between">
          <p className="text-gray-600">Manage your stock watchlist and price alerts</p>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowManageAlertsModal(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              View All Alerts
            </button>
            <button
              onClick={() => setShowAddStockModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Add to Watchlist
            </button>
          </div>
        </div>
      </div>

      {/* Plan Limits Display */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-blue-800">Plan:</span>
              <span className="text-sm text-blue-700">{formatPlanName(user?.subscription_plan)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-blue-800">Watchlist Items:</span>
              <span className="text-sm text-blue-700">
                {watchlist.length} / {planLimits.watchlistItems}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-blue-800">Price Alerts:</span>
              <span className="text-sm text-blue-700">
                {planLimits.priceAlerts}
              </span>
            </div>
          </div>
          {!planLimits.isUnlimitedWatchlist && watchlist.length >= planLimits.watchlistItems && (
            <div className="text-sm text-orange-600 font-medium">
              ⚠️ Watchlist limit reached. Upgrade your plan to add more items.
            </div>
          )}
        </div>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Bulk Actions */}
      {watchlist.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedItems.length === watchlist.length && watchlist.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="ml-2 text-sm text-gray-700">Select All</span>
              </label>
              {selectedItems.length > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedItems.length} item(s) selected
                </span>
              )}
            </div>
            {selectedItems.length > 0 && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Delete Selected ({selectedItems.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Watchlist Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-3 text-gray-600">Loading watchlist...</span>
        </div>
      ) : watchlist.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📈</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Your watchlist is empty</h3>
          <p className="text-gray-600 mb-6">Start building your watchlist by adding stocks you want to track.</p>
          <button
            onClick={() => setShowAddStockModal(true)}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Add Your First Stock
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === watchlist.length && watchlist.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Added Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {watchlist.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {item.stock_symbol}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => openAddAlertModal(item.stock_symbol, item.id)}
                        className="text-green-600 hover:text-green-900 transition-colors"
                      >
                        Add Alert
                      </button>
                      <button
                        onClick={() => openDeleteModal(item.stock_symbol, item.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <ManageAlertsModal
        isOpen={showManageAlertsModal}
        onClose={() => setShowManageAlertsModal(false)}
        planLimits={planLimits}
      />

      <AddStockModal
        isOpen={showAddStockModal}
        onClose={() => setShowAddStockModal(false)}
        onSuccess={handleAddStockSuccess}
        planLimits={planLimits}
        currentCount={watchlist.length}
      />

      <AddAlertModal
        isOpen={showAddAlertModal}
        onClose={() => setShowAddAlertModal(false)}
        stockSymbol={alertModalData.stockSymbol}
        itemId={alertModalData.itemId}
        onSuccess={handleAddAlertSuccess}
        planLimits={planLimits}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteItem}
        title="Delete Stock"
        message={`Are you sure you want to remove ${deleteModalData.stockSymbol} from your watchlist? This action cannot be undone.`}
        loading={deleteLoading}
      />

      <ConfirmationModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Selected Items"
        message={`Are you sure you want to delete ${selectedItems.length} selected item(s) from your watchlist? This action cannot be undone.`}
        loading={bulkDeleteLoading}
      />
    </div>
  );
};

// Main WatchlistPage Component
const WatchlistPage = () => {
  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Navbar Component */}
      <Navbar />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-5xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              {/* Sidebar - 1 column */}
              <AccountSidebar activePage="watchlist" />
              
              {/* Main Content - 3 columns */}
              <div className="md:col-span-3">
                <WatchlistContent />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default WatchlistPage;
